// TagPanel.jsx
import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import styles from "./styles.module.css";
import {
  listUserTags,
  createUserTag,
  updateUserTag,
  deleteUserTag,
  normalizeKey,
} from "./data";

const SEVERITIES = ["normal", "warning", "severe"];

const emptyForm = {
  mode: "create",            // "create" | "edit"
  label: "",
  default_status: "normal",
  _selectedKey: null,        // hidden; used internally for update/delete
};

export default function TagPanel({ open = false, onClose }) {
  const [isOpen, setIsOpen] = useState(!!open);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  const [tags, setTags] = useState([]);  // [{ key, label, default_status }]
  const [query, setQuery] = useState("");
  const [form, setForm] = useState(emptyForm);

  const panelRef = useRef(null);

  const close = useCallback(() => {
    setIsOpen(false);
    onClose?.();
  }, [onClose]);

  useEffect(() => setIsOpen(!!open), [open]);

  // ESC to close
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [close]);

  // Backdrop click to close
  useEffect(() => {
    const onDown = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) close();
    };
    document.addEventListener("mousedown", onDown, true);
    return () => document.removeEventListener("mousedown", onDown, true);
  }, [close]);

  // Open when SettingsPanel dispatches the event
  useEffect(() => {
    const handler = () => setIsOpen(true);
    window.addEventListener("open-manage-tags", handler);
    return () => window.removeEventListener("open-manage-tags", handler);
  }, []);

  // Load list whenever opened
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      setMsg(null);
      setLoading(true);
      try {
        const rows = await listUserTags();
        setTags(Array.isArray(rows) ? rows : []);
        if (rows && rows.length) {
          selectByKey(rows[0].key);
        } else {
          setForm({ ...emptyForm, mode: "create" });
        }
      } catch (e) {
        setMsg(e.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, [isOpen]);

  const onField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const selectByKey = (k) => {
    const r = tags.find((t) => t.key === k);
    if (!r) return;
    setForm({
      mode: "edit",
      label: r.label || "",
      default_status: r.default_status || "normal",
      _selectedKey: r.key, // hidden key retained in state
    });
  };

  const newTag = () => setForm({ ...emptyForm, mode: "create" });

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return tags;
    return tags.filter((t) =>
      (t.label || "").toLowerCase().includes(q) ||
      (t.key || "").toLowerCase().includes(q)   // still searchable by key (not shown)
    );
  }, [tags, query]);

  const save = async () => {
    setMsg(null);
    const label = String(form.label || "").trim();
    const default_status = SEVERITIES.includes(form.default_status) ? form.default_status : "normal";
    if (!label || label.length < 2) {
      setMsg("Please enter a label (min 2 characters).");
      return;
    }

    setLoading(true);
    try {
      if (form.mode === "create") {
        const key = normalizeKey(label); // hidden, server uses it as ID
        await createUserTag({ key, label, default_status });
        const rows = await listUserTags();
        setTags(rows);
        selectByKey(key);
        setMsg("Created.");
        setTimeout(() => setMsg(null), 1200);
      } else {
        await updateUserTag(form._selectedKey, { label, default_status });
        const rows = await listUserTags();
        setTags(rows);
        selectByKey(form._selectedKey);
        setMsg("Saved.");
        setTimeout(() => setMsg(null), 1200);
      }
    } catch (e) {
      setMsg(e.message || "Save failed");
    } finally {
      const detail = {};
      window.dispatchEvent(new CustomEvent("request-hits-refresh", { detail: { ...{detail}, at: Date.now() } }));
      setLoading(false);
    }
  };

  const remove = async () => {
    if (form.mode !== "edit") return;
    if (!window.confirm("Delete this tag from your library?\n\nIt will be unassigned from all vehicles.")) return;
    setLoading(true);
    setMsg(null);
    try {
      await deleteUserTag(form._selectedKey);
      const rows = await listUserTags();
      setTags(rows);
      newTag();
      setMsg("Deleted.");
      setTimeout(() => setMsg(null), 1200);
    } catch (e) {
      setMsg(e.message || "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        role="presentation"
        onClick={close}
        style={{ position: "fixed", inset: 0, background: "color-mix(in oklab, black 35%, transparent)", zIndex: 1067 }}
      />

      {/* Dialog */}
      <div
        ref={panelRef}
        className={styles.filterPanel}
        role="dialog"
        aria-modal="true"
        aria-label="Tags"
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: Math.min(860, window.innerWidth - 24 * 2),
          maxWidth: "96vw",
          height: "72vh", // fixed height
          display: "flex",
          flexDirection: "column",
          background: "#fff",
          zIndex: 1068,
          borderRadius: 12,
          boxShadow: "0 10px 30px rgba(0,0,0,.18)",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={styles.filterPanelHeader}
          style={{
            position: "sticky",
            top: 0,
            background: "#fff",
            zIndex: 1,
            borderBottom: "1px solid #eee",
          }}
        >
          <div className={styles.filterTitle}>Tags</div>
          <button type="button" className={styles.xBtn} onClick={close} aria-label="Close">×</button>
        </div>

        {/* Fixed-height content; only inner sections scroll */}
        <div
          className={styles.filterSection}
          style={{
            overflow: "hidden",       // prevent panel body from scrolling
            flex: "1 1 auto",
            padding: 16,
            display: "flex",
          }}
        >
          <div style={{ width: "100%" }}>
            <div className={styles.muted} style={{ minHeight: 18 }}>
              {loading ? "Working…" : msg || ""}
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "370px 1fr",
                gap: 16,
                alignItems: "stretch",
                height: "calc(100% - 18px)", // fill below msg line
                minHeight: 0,                // allow children to shrink for scrolling
              }}
            >
              {/* LEFT: list (compact, scrollable) */}
              <div
                style={{
                  border: "1px solid #eee",
                  borderRadius: 8,
                  padding: 8,
                  background: "#fff",
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0, // critical for flex child scrolling
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                    marginBottom: 8,
                    flex: "0 0 auto",
                  }}
                >
                  <input
                    className={styles.textInput}
                    placeholder="Search tags…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    style={{ height: 34, padding: "6px 10px" }}
                    aria-label="Search tags"
                  />
                  <button type="button" className={styles.applyBtn} onClick={newTag}>
                    New
                  </button>
                </div>

                <div
                  role="list"
                  aria-label="Tags"
                  style={{
                    flex: "1 1 auto",
                    overflow: "auto",   // the list is the scroller
                    minHeight: 0,
                    paddingRight: 2,    // small gutter to avoid scrollbar overlay
                  }}
                >
                  {filtered.length === 0 && (
                    <div className={styles.muted} style={{ padding: 8, textAlign: "center" }}>
                      {query ? "No matches." : "No tags yet."}
                    </div>
                  )}

                  {filtered.map((t) => {
                    const selected = form.mode === "edit" && form._selectedKey === t.key;
                    const status = (t.default_status || "normal").toLowerCase();
                    const pillCls = status === "severe" ? "bad" : status === "warning" ? "unk" : "ok";
                    return (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => selectByKey(t.key)}
                        role="listitem"
                        aria-selected={selected ? "true" : "false"}
                        style={{
                          width: "100%",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 8,
                          textAlign: "left",
                          border: "1px solid " + (selected ? "#c7d2fe" : "#eee"),
                          background: selected ? "#eef2ff" : "#fafafa",
                          borderRadius: 8,
                          padding: "8px 10px",
                          marginBottom: 8,
                          cursor: "pointer",
                        }}
                        title={t.label || ""}
                      >
                        <div
                          style={{
                            fontWeight: 600,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {t.label || "—"}
                        </div>
                        <div className={`status-pill ${pillCls}`} style={{ fontSize: 12 }}>
                          {status}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* RIGHT: editor (super simple) */}
              <div
                style={{
                  border: "1px solid #eee",
                  borderRadius: 8,
                  padding: 12,
                  background: "#fff",
                  overflow: "auto", // editor can scroll if very tall
                  minHeight: 0,
                }}
              >
                <div className={styles.fieldLabel} style={{ marginBottom: 6 }}>
                  {form.mode === "edit" ? "Edit tag" : "Create tag"}
                </div>

                <div className={styles.inputWrap}>
                  <label className={styles.fieldLabel}>Tag Name</label>
                  <input
                    className={styles.textInput}
                    value={form.label}
                    onChange={(e) => onField("label", e.target.value)}
                    placeholder="e.g. Priority"
                  />
                </div>

                <div className={styles.inputWrap}>
                  <label className={styles.fieldLabel}>Severity</label>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {SEVERITIES.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => onField("default_status", s)}
                        className={`status-pill ${s === "severe" ? "bad" : s === "warning" ? "unk" : "ok"}`}
                        aria-pressed={form.default_status === s}
                        style={{
                          border: form.default_status === s ? "3px solid #00000049" : "1px solid #ddd",
                          padding: "6px 10px",
                          borderRadius: 999,
                          cursor: "pointer",
                        }}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>

                <div className={styles.filterActions} style={{ marginTop: 12 }}>
                  {form.mode === "edit" ? (
                    <>
                      <button type="button" className={styles.resetBtn} onClick={remove}>
                        Delete
                      </button>
                      <div className={styles.flexSpacer} />
                      <button type="button" className={styles.applyBtn} onClick={save}>
                        Save
                      </button>
                    </>
                  ) : (
                    <>
                      <div className={styles.flexSpacer} />
                      <button type="button" className={styles.applyBtn} onClick={save}>
                        Create
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div> 
    </>
  );
}
